/**
 * Phase 86 Plan 86-04 Task 2 — the Compost design's top bar
 * (`.planning/design/mytasks-compost/README.md`, "Top bar contents"):
 * wordmark, breadcrumb, the dashed "internal view · staleness on" pill, the
 * search field (moved here from the old `MyTasksHeader`) and the avatar.
 *
 * The search field keeps its existing accessible label ("Search my tasks")
 * so any label-based query used elsewhere in the app or in tests keeps
 * working across the move (86-CONTEXT carry-forward #1).
 *
 * Phase 86 Plan 86-06 — the `⌘K` chip is now wired: this component forwards
 * a `ref` straight to the `<input>` element so `MyTasksPage` can hand it to
 * `useMyTasksKeyboard`, whose `⌘K`/`Ctrl+K` handler calls `.focus()` on it.
 * Only the focus behaviour is in scope — the phase context explicitly
 * defers anything resembling a command palette beyond that.
 */
import { forwardRef } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../ui/Avatar';
import { MICRO_LABEL_GREY_HEX, DASHED_BORDER_GREY_HEX } from './compostTokens';

export interface MyTasksTopBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  /** Rendered into the avatar; falls back to a neutral label when empty. */
  userDisplayName: string;
  /**
   * 260915-i0x — the signed-in user's avatar color slot. Null while
   * impersonating (the name shown is the target's, so the real user's slot
   * would colour the wrong person) — Avatar then hashes the name.
   */
  userColorSeq?: number | null;
  /**
   * MKS-203 — the signed-in user's effective avatar photo URL. Null while
   * impersonating, same reasoning as `userColorSeq` above.
   */
  userAvatarUrl?: string | null;
}

export const MyTasksTopBar = forwardRef<HTMLInputElement, MyTasksTopBarProps>(function MyTasksTopBar(
  { search, onSearchChange, userDisplayName, userColorSeq, userAvatarUrl },
  searchInputRef,
) {
  return (
    <div
      data-testid="my-tasks-top-bar"
      className="flex items-center gap-3.5 px-[18px] py-[11px] border-b border-ms-border bg-ms-surface shrink-0"
    >
      <span
        className="font-mono text-[11px] font-bold tracking-[0.1em] text-ms-orange"
        data-testid="my-tasks-top-bar-wordmark"
      >
        MONKSHEETS
      </span>
      <span className="font-mono text-[11px]" style={{ color: MICRO_LABEL_GREY_HEX }}>
        / my-tasks
      </span>
      <span
        className="px-2 py-[3px] rounded-full font-mono text-[10px] font-medium whitespace-nowrap"
        style={{
          border: `1px dashed ${DASHED_BORDER_GREY_HEX}`,
          color: MICRO_LABEL_GREY_HEX,
        }}
      >
        internal view · staleness on
      </span>

      <div className="ml-auto flex items-center gap-2">
        <div className="relative">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: MICRO_LABEL_GREY_HEX }}
          />
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="search or paste a code"
            aria-label="Search my tasks"
            className="w-64 pl-7 pr-9 py-[5px] rounded-[5px] border border-ms-border bg-ms-canvas font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-ms-orange/30"
            style={{ color: MICRO_LABEL_GREY_HEX }}
          />
          <span
            aria-hidden="true"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 px-[5px] py-[1px] rounded-[3px] text-[9.5px]"
            style={{ border: `1px solid ${DASHED_BORDER_GREY_HEX}`, color: '#7A756C' }}
          >
            ⌘K
          </span>
        </div>
        <Avatar
          name={userDisplayName || '?'}
          colorSeq={userColorSeq}
          avatarUrl={userAvatarUrl}
          size="sm"
        />
      </div>
    </div>
  );
});
